/**
 * Thin loader + minimal typings for the official YouTube IFrame Player API.
 *
 * Browser-build Learn feed only: real players (muted autoplay on the active
 * card, cue-ahead on the next) need the API; the Telegram build stays on
 * tap-to-play bare embeds and never loads this script. Mirrors the iOS
 * app's YouTubePlayerPool architecture (one managed player per live cell).
 */

export interface YtPlayer {
  playVideo(): void;
  pauseVideo(): void;
  mute(): void;
  unMute(): void;
  seekTo(seconds: number, allowSeekAhead: boolean): void;
  destroy(): void;
}

export interface YtPlayerEvent {
  target: YtPlayer;
  data: number;
}

interface YtNamespace {
  Player: new (
    element: HTMLElement,
    options: {
      videoId: string;
      playerVars?: Record<string, string | number>;
      events?: {
        onReady?: (event: YtPlayerEvent) => void;
        onStateChange?: (event: YtPlayerEvent) => void;
        onError?: (event: YtPlayerEvent) => void;
      };
    },
  ) => YtPlayer;
  PlayerState: { ENDED: number; PLAYING: number };
}

declare global {
  interface Window {
    YT?: YtNamespace;
    onYouTubeIframeAPIReady?: () => void;
  }
}

let apiPromise: Promise<YtNamespace> | null = null;

/**
 * Rejects when the API script can't load — Brave shields and some adblockers
 * block www.youtube.com/iframe_api on third-party sites (verified against
 * join.freshman.app 9 Aug 2026) while still allowing plain embed iframes.
 * Callers fall back to the tap-to-play bare embed on rejection; the rejected
 * promise is cached so every later card degrades instantly.
 */
export function ensureYouTubeApi(): Promise<YtNamespace> {
  if (apiPromise) return apiPromise;
  apiPromise = new Promise((resolve, reject) => {
    if (window.YT?.Player) {
      resolve(window.YT);
      return;
    }
    const timer = setTimeout(() => reject(new Error("yt-api-timeout")), 5000);
    const previous = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      previous?.();
      clearTimeout(timer);
      resolve(window.YT!);
    };
    const script = document.createElement("script");
    script.src = "https://www.youtube.com/iframe_api";
    script.onerror = () => {
      clearTimeout(timer);
      reject(new Error("yt-api-blocked"));
    };
    document.head.appendChild(script);
  });
  return apiPromise;
}

/** Session-wide sound preference: once the user unmutes any video, later
 * actives start with sound (the page has a gesture by then, so browsers
 * generally allow it; where they don't, the card just stays muted with the
 * unmute control showing). */
export const soundPreference = { on: false };
